import { useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import type { WorkspaceRole } from "@shared/types/supabase.types";
import { createContainerInvite } from "@/features/superuser/invites";
import { inviteQueries } from "@/features/workspace-invites/api/workspace-invites-keys";
import { containerQueries } from "@/features/containers/api/containers-keys";
import { ACTIVE_PROJECT_SLUG_KEY, ACTIVE_WORKSPACE_SLUG_KEY } from "@/lib/local-storage.consts";

type CreateWorkspaceInviteMutationInput = {
  email?: string | null;
  domain?: string | null;
  role: WorkspaceRole;
  multipleUse?: number;
  canCreateWorkspace?: boolean;
};

export function useGoToWorkspace() {
  const navigate = useNavigate();

  const goToWorkspace = useCallback(
    (slug: string | undefined) => {
      // Special-case "All workspaces" selection: navigate to the workspaces index
      if (slug === "/") {
        localStorage.removeItem(ACTIVE_WORKSPACE_SLUG_KEY);
        localStorage.removeItem(ACTIVE_PROJECT_SLUG_KEY);
        void navigate({ to: "/workspaces" });
        return;
      }

      if (!slug) return;
      localStorage.setItem(ACTIVE_WORKSPACE_SLUG_KEY, slug);
      localStorage.removeItem(ACTIVE_PROJECT_SLUG_KEY);
      void navigate({ to: "/workspaces/$slug", params: { slug } });
    },
    [navigate],
  );

  return { goToWorkspace };
}

export function useCreateWorkspaceInviteMutation(workspaceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      email = null,
      domain = null,
      role,
      multipleUse = 1,
      canCreateWorkspace = false,
    }: CreateWorkspaceInviteMutationInput) =>
      createContainerInvite({
        containerId: workspaceId,
        email,
        domain,
        role,
        multipleUse,
        canCreateWorkspace,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: inviteQueries.all(workspaceId),
      });
      void queryClient.invalidateQueries({
        queryKey: [...containerQueries.memberships(), workspaceId],
      });
    },
  });
}
